import { FormInputData, ValidFormData } from 'hooks/useTypedForm';
import * as Yup from 'yup';
import { dimensionFormConstants } from 'utils/unitsOfMeasure/constants';
import {
  quantFieldSchema,
  dimensionSchemasMapped,
} from 'utils/unitsOfMeasure/schemas';

export enum AdvancedFieldKey {
  NEED_DELIVERY_BY = 'needDeliveryBy',
  REQUESTED_NOTES = 'requestedNotes',
  PRODUCTION_RUN = 'productionRun',
  TEAM = 'team',
  TAGS = 'tags',
  ORG_SUPPLIER = 'orgSupplier',
  TARGET_PRICE = 'targetPrice',
  PRINTED_NOTES = 'printedNotes',
}

export const formConstants = {
  ...dimensionFormConstants,
  purchaseRequestGroup: {
    id: 'purchaseRequestGroup' as const,
    label: 'Cart',
  },
  cartName: { id: 'cartName' as const, label: 'New Cart Name' },
  purchaseRequestGroupId: {
    id: 'purchaseRequestGroupId' as const,
    label: 'Cart',
  },
  needDeliveryBy: {
    id: 'needDeliveryBy' as const,
    label: 'Target Delivery Date',
  },
  requestedNotes: { id: 'requestedNotes' as const, label: 'Notes' },
  productionRun: {
    id: 'productionRun' as const,
    label: 'Program',
  },
  team: { id: 'team' as const, label: 'Team' },
  tags: { id: 'tags' as const, label: 'Tags' },
  supplierObjectId: { id: 'supplierObjectId' as const, label: 'Supplier' },
  supplierName: { id: 'supplierName' as const, label: 'Supplier' },
  orgSupplierId: { id: 'orgSupplierId' as const, label: 'Supplier' },
  targetPrice: { id: 'targetPrice' as const, label: 'Target Price' },
  orgSupplier: { id: 'orgSupplier' as const, label: 'Supplier' },
} as const;

export const createBasePartFormSchema = () =>
  Yup.object({
    [formConstants.quant.id]: quantFieldSchema,
    ...dimensionSchemasMapped,
  });

export const createSharedOptionsSchema = () =>
  Yup.object({
    [formConstants.needDeliveryBy.id]: Yup.date()
      .nullable()
      .typeError('Invalid date format'),
    [formConstants.requestedNotes.id]: Yup.string().nullable(),
    [formConstants.productionRun.id]: Yup.string().nullable(),
    [formConstants.team.id]: Yup.string().nullable(),
    [formConstants.tags.id]: Yup.array().of(Yup.string()).nullable(),
    [formConstants.orgSupplier.id]: Yup.string().nullable(),
  });

export const advancedSettingsFormValidationSchema = Yup.object({
  [formConstants.needDeliveryBy.id]: Yup.date()
    .nullable()
    .typeError('Invalid date format'),
  [formConstants.requestedNotes.id]: Yup.string().nullable(),
  [formConstants.productionRun.id]: Yup.string().nullable(),
  [formConstants.team.id]: Yup.string().nullable(),
  [formConstants.orgSupplierId.id]: Yup.string().nullable(),
  [formConstants.targetPrice.id]: Yup.number()
    .nullable()
    .min(0, 'Target price must be greater than or equal to 0'),
});

export const addToPurchaseRequestGroupFormValidationSchema = Yup.object({
  [formConstants.quant.id]: quantFieldSchema,
  [formConstants.purchaseRequestGroup.id]: Yup.string().nullable(),
  [formConstants.cartName.id]: Yup.string().nullable(),
  [formConstants.purchaseRequestGroupId.id]: Yup.string().nullable(),
  [formConstants.needDeliveryBy.id]: Yup.date()
    .nullable()
    .typeError('Invalid date format'),
  [formConstants.requestedNotes.id]: Yup.string().nullable(),
  [formConstants.productionRun.id]: Yup.string().nullable(),
  [formConstants.team.id]: Yup.string().nullable(),
  [formConstants.tags.id]: Yup.array().of(Yup.string()).nullable(),
  [formConstants.supplierObjectId.id]: Yup.string().nullable(),
  [formConstants.supplierName.id]: Yup.string().nullable(),
  [formConstants.orgSupplierId.id]: Yup.string().nullable(),
  [formConstants.targetPrice.id]: Yup.number()
    .nullable()
    .min(0, 'Target price must be greater than or equal to 0'),
  ...dimensionSchemasMapped,
}).test(
  'purchase-request-group-selection',
  'Either name or existing group must be selected',
  (value) => {
    const hasName = !!value[formConstants.cartName.id];
    const hasId = !!value[formConstants.purchaseRequestGroupId.id];
    return hasName || hasId;
  }
);

export type addToPurchaseRequestGroupFormValidFormData = ValidFormData<
  typeof addToPurchaseRequestGroupFormValidationSchema
>;
export type addToPurchaseRequestGroupFormFormInputData = FormInputData<
  typeof addToPurchaseRequestGroupFormValidationSchema
>;
